import Accordion from '../../components/Accordion/Accordion';
import Button from 'tailwind-components/Button/Button';
import Card from 'components/Card/Card';
import CarrierCard from './CarrierCard/CarrierCard';
import companyNames from 'constants/companyNames';
import ContactCard from './ContactCard/ContactCard';
import HiddenSideSection from 'tailwind-components/HiddenSideSection/HiddenSideSection';
import HtmlContent from 'tailwind-components/HtmlContent/HtmlContent';
import MainSectionCarriers from '../../components/MainSectionCarriers/MainSectionCarriers';
import React, { useEffect, useState } from 'react';
import Tabs from 'components/Tabs/Tabs';
import axios from 'axios';
import propTypes from 'prop-types';
import style from './carrierResources.module.scss';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { Tooltip } from '@mui/material';

function CarrierResources({
  carriers,
  appointedCarriers,
  carrierFaqs,
  getAppointmentsViewData,
  getCarrierFaqs,
  setLocalLoader,
  localLoader,
  carrierLoader
}) {
  const [message, setMessage] = useState('');
  const [active, setActive] = useState({
    name: '',
    type: ''
  });
  const [currentCarrier, setCurrentCarrier] = useState({});
  const cancelTokenSource = axios.CancelToken.source();
  const [floatingTabs, setFloatingTabs] = useState(false);

  useEffect(() => {
    getAppointmentsViewData(setLocalLoader, cancelTokenSource, true);
    return () => {
      cancelTokenSource.cancel('API Call Canceled');
    };
  }, []);

  useEffect(() => {
    if (active.type) {
      setMessage('No information available for this carrier');
      setCurrentCarrier(carriers[active.type][active.name]);
    }
  }, [active.type, active.name]);

  useEffect(() => {
    if (currentCarrier.id) {
      getCarrierFaqs(currentCarrier.id, cancelTokenSource);
      return () => {
        cancelTokenSource.cancel('API Call Canceled');
      };
    }
  }, [currentCarrier]);

  const handleOpenLink = url => {
    window.open(url, 'blank');
  };

  const handleScroll = e => {
    if (e.target.scrollTop > 180) {
      setFloatingTabs(true);
    } else {
      setFloatingTabs(false);
    }
  };

  const handleResetActiveCarrier = () => {
    setActive({
      name: '',
      type: ''
    });
  };
  return (
    <div
      className={`${style.carrierResources} ${
        active.name ? style.isActive : ''
      }`}
    >
      <div className={style.carrierResourcesItem}>
        <Card fill title='Carrier Resources' onScrollCapture={handleScroll}>
          <Tabs
            floatingHeader={floatingTabs}
            headers={['Appointed Carriers', 'All Carriers']}
          >
            <MainSectionCarriers
              carriers={appointedCarriers}
              active={active.name}
              setActive={setActive}
              Card={CarrierCard}
              CardNoStates={CarrierCard}
              localLoader={localLoader}
              message={`No appointed carriers - Click on the "All Carriers" tab above to view all ${companyNames.short} carriers`}
              simplified
            />
            <MainSectionCarriers
              carriers={carriers}
              active={active.name}
              setActive={setActive}
              Card={CarrierCard}
              CardNoStates={CarrierCard}
              localLoader={localLoader}
              simplified
            />
          </Tabs>
        </Card>
      </div>
      <div className={style.detailsItem}>
        {active.type &&
        (currentCarrier.carrierBrokerPortal ||
          currentCarrier.brokerSupportNumber ||
          currentCarrier.memberSupportNumber ||
          currentCarrier.description ||
          currentCarrier.enrollmentPortal ||
          currentCarrier.omWebsiteLink) ? (
          <Card
            fill
            title={active.name}
            leftOptions={
              <Button
                size='small'
                variant='default'
                icon={<ArrowLeft size={24} />}
                rounded='large'
                outlined
                onClick={handleResetActiveCarrier}
              />
            }
            options={
              currentCarrier.website && (
                <Button
                  size='xsmall'
                  variant='secondary'
                  icon={<ExternalLink size={20} />}
                  rounded={'large'}
                  text={'Website'}
                  onClick={() => handleOpenLink(currentCarrier.website)}
                />
              )
            }
          >
            <section className={style.sideSection}>
              <div className={style.description}>
                <h3>Description</h3>
                {currentCarrier.description ? (
                  <HtmlContent
                    html={currentCarrier.description}
                    className='mt-2.5 text-left text-grey'
                  />
                ) : (
                  'No description available'
                )}
              </div>

              {(currentCarrier.brokerSupportNumber ||
                currentCarrier.memberSupportNumber ||
                currentCarrier.Broker_Support_Email ||
                currentCarrier.Broker_Commission_Email) && (
                <div className={style.contactInfo}>
                  <h3>Contact Information</h3>
                  <div style={{ gap: '15px' }}>
                    {currentCarrier.brokerSupportNumber && (
                      <ContactCard
                        title='Broker Support Phone Number'
                        info={currentCarrier.brokerSupportNumber}
                      />
                    )}
                    {currentCarrier.memberSupportNumber && (
                      <ContactCard
                        title='Member Support Phone Number'
                        info={currentCarrier.memberSupportNumber}
                      />
                    )}
                    {currentCarrier.Broker_Support_Email && (
                      <ContactCard
                        title='Broker Support Email'
                        info={currentCarrier.Broker_Support_Email.toLowerCase()}
                        hasButton={false}
                      />
                    )}
                    {currentCarrier.Broker_Commission_Email && (
                      <ContactCard
                        title='Broker Commission Email'
                        info={currentCarrier.Broker_Commission_Email.toLowerCase()}
                        hasButton={false}
                      />
                    )}
                  </div>
                </div>
              )}
              <div className={style.externalLinks}>
                <h3>External links</h3>
                {currentCarrier.username && currentCarrier.password && (
                  <>
                    <h2>Username</h2>
                    <span>{currentCarrier.username}</span>
                    <h2>Password</h2>
                    <span style={{ paddingBottom: 10 }}>
                      {currentCarrier.password}
                    </span>
                    {/* DO NOT REMOVE: helps to space out content */}
                    <div />
                  </>
                )}
                {currentCarrier.carrierBrokerPortal && (
                  <div>
                    <span>Broker portal</span>
                    <Button
                      variant='secondary'
                      size='xsmall'
                      icon={<ExternalLink size={20} />}
                      text='Website'
                      rounded={'large'}
                      onClick={() =>
                        handleOpenLink(currentCarrier.carrierBrokerPortal)
                      }
                    />
                  </div>
                )}
                {currentCarrier.enrollmentPortal && (
                  <div>
                    <span>Enrollment portal</span>
                    <Button
                      variant='secondary'
                      size='xsmall'
                      icon={<ExternalLink size={20} />}
                      text='Website'
                      rounded={'large'}
                      onClick={() =>
                        handleOpenLink(currentCarrier.enrollmentPortal)
                      }
                    />
                  </div>
                )}
                {currentCarrier.omWebsiteLink && (
                  <div>
                    <span>Website Link</span>
                    <Button
                      variant='secondary'
                      size='xsmall'
                      icon={<ExternalLink size={20} />}
                      text='Website'
                      rounded={'large'}
                      onClick={() =>
                        handleOpenLink(currentCarrier.omWebsiteLink)
                      }
                    />
                  </div>
                )}
                {carrierFaqs && carrierFaqs.length > 0 && (
                  <>
                    <h3>Carrier FAQ&apos;s</h3>
                    <Accordion
                      ItemsArray={carrierFaqs}
                      loading={carrierLoader}
                      tittle="Carrier FAQ's"
                    />
                  </>
                )}
              </div>
            </section>
          </Card>
        ) : (
          <>
            <HiddenSideSection text={message || null} />
            {active.name && (
              <div className={style.closeButton}>
                <Tooltip title={'Close'}>
                  <div>
                    <Button
                      size='xsmall'
                      variant='default'
                      icon={<ArrowLeft size={24} />}
                      text={'Close'}
                      rounded='large'
                      outlined
                      onClick={handleResetActiveCarrier}
                    />
                  </div>
                </Tooltip>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

CarrierResources.propTypes = {
  carriers: propTypes.object,
  appointedCarriers: propTypes.object,
  carrierFaqs: propTypes.object,
  user: propTypes.object,
  getAppointmentsViewData: propTypes.func,
  getCarrierFaqs: propTypes.func,
  setLocalLoader: propTypes.func,
  localLoader: propTypes.bool,
  carrierLoader: propTypes.bool
};

export default CarrierResources;
